import {
	Badge,
	Banner,
	Button,
	Cell,
	Placeholder,
	Section,
	Spinner,
} from '@telegram-apps/telegram-ui'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Page } from 'shared/components/Page'
import { useMobileTrpc } from 'shared/api'
import { formatUzsAmount } from 'shared/lib/format'
import { useTranslate } from 'shared/lib/i18n'
import type { TranslationPaths } from 'shared/lib/i18n'
import { MIN_TOPUP_UZS, TopupPanel } from './TopupPanel'

// wallet.Get returns at most this many recent transactions; older ones are
// loaded on demand through the paginated wallet.History endpoint.
const RECENT_LIMIT = 10

const WALLET_TRANSACTION_KEYS = {
	topup: 'status.walletTransaction.topup',
	purchase: 'status.walletTransaction.purchase',
	refund: 'status.walletTransaction.refund',
	bonus: 'status.walletTransaction.bonus',
	adjustment: 'status.walletTransaction.adjustment',
} as const satisfies Record<string, TranslationPaths>

export function Wallet() {
	const trpc = useMobileTrpc()
	const navigate = useNavigate()
	const { t } = useTranslate()
	const [topupOpen, setTopupOpen] = useState(false)
	const [historyExpanded, setHistoryExpanded] = useState(false)
	const { data, isLoading, isError } = useQuery(trpc.wallet.Get.queryOptions())

	const history = useInfiniteQuery({
		...trpc.wallet.History.infiniteQueryOptions(
			{ limit: 20 },
			{
				getNextPageParam: (last) => last.nextCursor,
				initialCursor: 0,
			},
		),
		enabled: historyExpanded,
	})
	// Until the first History page arrives, keep showing the recent list from
	// wallet.Get so the section doesn't flicker away on expand.
	const transactions =
		historyExpanded && history.data
			? history.data.pages.flatMap((p) => p.items)
			: (data?.recent ?? [])
	const showMore =
		historyExpanded && history.data
			? history.hasNextPage
			: (data?.recent.length ?? 0) >= RECENT_LIMIT
	const loadingMore = history.isFetching

	function handleShowMore() {
		if (!historyExpanded) setHistoryExpanded(true)
		else void history.fetchNextPage()
	}

	return (
		<Page>
			<div className='account-page p-4 pb-10'>
				<h1 className='mb-4 text-xl font-semibold text-foreground'>
					{t('navigation.wallet')}
				</h1>

				{isLoading && (
					<div className='flex justify-center py-12'>
						<Spinner size='l' />
					</div>
				)}
				{isError && (
					<Placeholder
						header={t('errors.loadFailed')}
						description={t('errors.tryLater')}
					/>
				)}

				{data && (
					<>
						<div className='mb-4 rounded-xl bg-[var(--tgui--section_bg_color)] p-4 text-center'>
							<div className='text-sm text-muted-foreground'>
								{t('pages.wallet.balance')}
							</div>
							<div className='text-3xl font-bold text-foreground'>
								{t('common.amountUzs', {
									amount: formatUzsAmount(data.balanceUzs),
								})}
							</div>
							<Button
								size='l'
								stretched
								className='mt-3'
								onClick={() => setTopupOpen((v) => !v)}
							>
								{t('pages.wallet.topup')}
							</Button>
						</div>

						{topupOpen && <TopupPanel onDone={() => setTopupOpen(false)} />}

						{data.pendingTopups.length > 0 && (
							<Section
								className='account-card'
								header={t('pages.wallet.pendingTopups')}
							>
								{data.pendingTopups.map((topup) => (
									<Cell
										key={topup._id}
										subtitle={new Date(topup.createdAt).toLocaleString('ru-RU')}
										after={
											<Badge type='number' mode='secondary'>
											{t('pages.wallet.checking')}
											</Badge>
										}
									>
										{t('common.amountUzs', {
											amount: formatUzsAmount(topup.amountUzs),
										})}
									</Cell>
								))}
							</Section>
						)}

						{transactions.length > 0 && (
							<Section className='account-card' header={t('pages.wallet.history')}>
								{transactions.map((tx) => (
									<Cell
										key={tx._id}
										onClick={
											tx.lotId ? () => navigate(`/lot/${tx.lotId}`) : undefined
										}
										className={
											tx.lotId
												? 'cursor-pointer hover:bg-black/5 dark:hover:bg-white/5'
												: undefined
										}
										subtitle={new Date(tx.createdAt).toLocaleString('ru-RU')}
										after={
											<span
												className={
													tx.amountUzs > 0
														? 'text-green-600'
														: 'text-foreground'
												}
											>
												{tx.amountUzs > 0 ? '+' : ''}
											{t('common.amountUzs', {
												amount: formatUzsAmount(tx.amountUzs),
											})}
											</span>
										}
									>
											<span>
												{t(WALLET_TRANSACTION_KEYS[tx.type])}
											</span>
										{tx.orderNumber != null && (
											<span className='ml-1.5 text-xs font-mono text-muted-foreground'>
													{t('pages.wallet.orderReference', {
													number: String(tx.orderNumber),
													})}
											</span>
										)}
									</Cell>
								))}
								{showMore && (
									<Button
										mode='plain'
										size='m'
										stretched
										loading={loadingMore}
										onClick={handleShowMore}
									>
											{t('pages.wallet.showMore')}
									</Button>
								)}
							</Section>
						)}

						{data.recent.length === 0 && data.pendingTopups.length === 0 && (
							<>
								<Banner
									type='section'
									header={t('pages.wallet.tezIdBonusTitle')}
									className='mt-4'
								>
									{t('pages.wallet.tezIdBonusDescription')}
								</Banner>

								<Placeholder
									header={t('pages.wallet.empty')}
									description={t('pages.wallet.emptyDescription', {
										amount: t('common.amountUzs', {
											amount: formatUzsAmount(MIN_TOPUP_UZS),
										}),
									})}
								/>
							</>
						)}
					</>
				)}
			</div>
		</Page>
	)
}
